import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/popover';
import { CloseIcon, LucyIcon, RightArrowIcon } from '@sketchnote/icons';
import { Button } from '@sketchnote/shared-components';
import { TRenderElementProps, useEditorRef } from '@udecode/plate';
import { backendUrl } from 'config';
import * as React from 'react';
import { Editor, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { EventSourceAPI } from 'src/api';
import { useAuth } from 'src/shared/hooks/use-auth';

import { promptOptions } from './aiprompts';

const { memo, useState } = React;

export const AiContentElement = memo(({ children, attributes, element }: TRenderElementProps) => {
  const [prompt, setPrompt] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [responseText, setResponseText] = useState<string>('');
  const editor = useEditorRef();
  const inputRef = React.useRef(null);
  const [isError, setIsError] = useState<boolean>(false);
  let splitAIText = [];
  const isPublicFile = useAuth((s) => s.isPublicFile);

  const handlePrompt = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrompt(e.target.value);
  };

  const handleSubmit = async (e: any) => {
    setIsLoading(true);
    e.preventDefault();
    const path = ReactEditor.findPath(editor, element);
    let currpath = ReactEditor.findPath(editor, element);

    const aiEventResponse = EventSourceAPI(
      `${backendUrl}api/files/ai?query=${encodeURIComponent(prompt)}`,
      {}
    );

    aiEventResponse.onmessage = (e) => {
      if (e.data == '[DONE]') {
        aiEventResponse.close();
        Transforms.removeNodes(editor, { at: path });
        // const splitAIText = responseText.split('\n');

        splitAIText.forEach((text) => {
          Transforms.insertNodes(
            editor,
            {
              type: 'p',
              children: [
                {
                  text: text,
                },
              ],
            } as any,
            { at: currpath }
          );
          currpath = Path.next(currpath);
        });

        //For selecting focus on end
        const [, end] = Editor.edges(editor, Path.previous(currpath));
        if (end) {
          Transforms.select(editor, end);
        } else {
          Transforms.insertNodes(
            editor,
            {
              type: 'p',
              children: [
                {
                  text: '',
                },
              ],
            } as any,
            { at: currpath }
          );
          Transforms.select(editor, currpath);
        }
        ReactEditor.focus(editor);
      } else {
        splitAIText = e.data.split('/[n]');
        const d = e.data.split('/[n]').join('\n');
        setResponseText(d);
      }
    };
  };

  const setFocus = () => {
    if (inputRef.current) {
      (inputRef.current as HTMLInputElement).focus();
    }
  };

  React.useEffect(() => {
    // Focus fix for firefox
    setTimeout(() => setFocus(), 1);
  }, []);

  const onSelectPrePrompt = (e) => {
    const buttonValue = e.target.getAttribute('data-value');
    const prefixPresetIndex = promptOptions.findIndex((p) => prompt.startsWith(p.value));
    if (prefixPresetIndex < 0) {
      setPrompt((prev) => buttonValue + prev);
    } else {
      const currentPrefixValue = promptOptions[prefixPresetIndex].value;
      const promptWithoutPrefix = prompt.substr(currentPrefixValue.length);
      setPrompt(buttonValue + promptWithoutPrefix);
    }
    setFocus();
  };

  if (isPublicFile) return <div>{children}</div>;

  return (
    <div {...attributes} contentEditable={false}>
      {isLoading ? (
        <div>
          <div className="flex text-left w-full rounded-sm">
            <p className="leading-24">
              {responseText || (
                <div className="w-full flex p-8 items-center">
                  <LucyIcon className="mr-6 animate-pulse" size={24} />
                  <span className="text-gray-400 font-light text-sm">
                    {`I'm putting the finishing touches on your content now...`}
                  </span>
                </div>
              )}
            </p>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <form
            className="border border-gray-200 shadow w-full flex p-8 rounded-lg mt-4 mb-8 items-center h-44"
            onSubmit={handleSubmit}
          >
            <LucyIcon className="mr-4" size={24} />
            <input
              type="text"
              onChange={handlePrompt}
              className="px-8 py-2 w-full mr-8 text-sm placeholder-gray-400"
              placeholder="Let's create something awesome! What do you need?"
              value={prompt}
              ref={inputRef}
              maxLength={1024}
            />
            {prompt && (
              <Button type="submit" className="text-center">
                <RightArrowIcon />
              </Button>
            )}
          </form>
          <div className="flex justify-between">
            <div>
              {promptOptions.slice(0, 5).map((item) => (
                <button
                  key={item.id}
                  data-value={item.value}
                  onClick={onSelectPrePrompt}
                  className="border rounded-sm mr-8 px-6 py-2 text-xs text-gray-600 font-medium hover:bg-gray-100"
                >
                  {item.label}
                </button>
              ))}
              <Popover>
                <PopoverTrigger>
                  <button className="border rounded-sm mr-8 px-6 py-2 text-xs text-gray-600 font-medium hover:bg-gray-100">
                    ...
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <div className="p-4 text-left rounded-sm shadow flex flex-col border border-gray-200 bg-white">
                    <div className=" text-gray-500 mb-4 px-4 text-xs">Draft with Lucy</div>
                    <div className="flex flex-col">
                      {promptOptions.slice(5, promptOptions.length).map((item) => (
                        <button
                          key={item.id}
                          data-value={item.value}
                          onClick={onSelectPrePrompt}
                          className="w-full rounded-sm mr-8 px-4 py-4 text-xs font-medium text-gray-600 hover:bg-gray-100 text-left"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <span className="text-xs text-gray-400 mr-6">{prompt.length}/1024</span>
            </div>
          </div>
          {isError && (
            <div className="w-full bg-gray-100 text-red-600 p-8 text-xs mt-6 flex justify-between items-center">
              <span>{`Sorry, I couldn't process your request. Can you please try a different phrasing or provide more information?`}</span>
              <CloseIcon className="cursor-pointer" onClick={() => setIsError(false)} />
            </div>
          )}
        </div>
      )}
      <div className="hidden">{children}</div>
    </div>
  );
});

AiContentElement.displayName = 'AiContentElement';
